import _ from 'lodash';

import {hasValue} from '@packages/systems/core/utils/JsUtils';
import * as CssUtils from '@packages/systems/style-legacy/utils';
import {splitCssValue} from '@packages/systems/core/utils/CssStringUtils';
import {strRight} from '@packages/utilities/string-utils';
import * as StreamTypes from '../constants/StreamTypes';

/**
 * Contains shared parsing methods.
 */

/**
 * Characters that are trimmed off from commented part items.
 */
const COMMENT_CHARS = '/*,; ';

function makePartJsonMode() {
  return {
    _hidden: false,
  };
}
function partJsonLocal(partJson) {
  return partJson.local || (partJson.local = makePartJsonMode());
}
function partJsonUpstream(partJson) {
  return partJson.upstream || (partJson.upstream = makePartJsonMode());
}
function partJsonDownstream(partJson) {
  return partJson.downstream || (partJson.downstream = makePartJsonMode());
}
function partJsonBrowser(partJson) {
  return partJson.browser || (partJson.browser = makePartJsonMode());
}
const NONE = 'none';
const NORMAL = 'normal';

/**
 * Parses style data for single-value multi-part values (box-shadow, text-shadow, transform, filter).
 * @param  {Object}   styleData   The original values from StyleUtils.buildStyleMap.
 * @param  {String}   separator   The character to break parts on. Should be ',' for shadows, ' ' for transforms, etc.
 * @param  {Function} decorator   A callback to call to decorate each part JSON with additional properties.
 * @return {Object}               Parsed JSON part information.
 */
export function parseSimpleParts(styleData, separator, decorator) {
  const COMMENT_START = '/*';

  let local = [];
  let upstream = [];
  let downstream = [];

  let explicitLocalNoneOrNormal = false;
  let explicitUpstreamNoneOrNormal = false;
  let explicitNoneOrNormalSource = null;

  /**
   * styleData is in the following format (e.g. for box-shadow):
   *
   * upstream: { value: 'inset 1px 0 0 0 blue, 10px -1px 0 2px rgba(255, 0, 0, 0.5)', source: StyleRule },
   * local: { value: 'inset 1px 0 0 0 green' }
   */

  // Walk through each cascade mode (upstream, local, downstream) and each subvalue (property, duration, etc)
  // and split values up into discrete chunks
  const split = {};
  _.each(StreamTypes.CASCADE_CHAIN_BROWSER, function (mode) {
    // Look up the mode (e.g. 'upstream') and see if there's any CSS style data applied
    const modeData = styleData[mode];
    if (!modeData) {
      return;
    }

    let cssValue = modeData.value || '';
    const isUpstream = mode === StreamTypes.UPSTREAM;

    // Determine if the CSS value has a "none" override with other stuff (e.g. commented parts) afterwards
    if (_.startsWith(cssValue, NONE + ' ' + COMMENT_START)) {
      if (isUpstream) {
        explicitUpstreamNoneOrNormal = true;
      } else {
        explicitLocalNoneOrNormal = true;
      }
      explicitNoneOrNormalSource = modeData.source;

      cssValue = _.trim(strRight(cssValue, NONE + (isUpstream ? '' : ' ')));
    } else if (_.startsWith(cssValue, NORMAL + ' ' + COMMENT_START)) {
      if (isUpstream) {
        explicitUpstreamNoneOrNormal = true;
      } else {
        explicitLocalNoneOrNormal = true;
      }
      explicitNoneOrNormalSource = modeData.source;

      cssValue = _.trim(strRight(cssValue, NORMAL + (isUpstream ? '' : ' ')));
    }

    split[mode] = splitCssValue(cssValue, separator);
  });

  function parseMode(mode) {
    const splitValues = split[mode];

    const parts = [];

    // If there is a none reset, use that
    if (
      splitValues &&
      splitValues.length > 0 &&
      (splitValues[0] === NONE || splitValues[0] === NORMAL)
    ) {
      const isUpstream = mode === StreamTypes.UPSTREAM;

      if (isUpstream) {
        explicitUpstreamNoneOrNormal = true;
      } else {
        explicitLocalNoneOrNormal = true;
      }
      const modeData = styleData[mode];
      // No need to check 'modeData' as it should always exist
      explicitNoneOrNormalSource = modeData.source;

      return parts;
    }

    // Create a JSON object for each part in the list
    for (let i = 0; i < splitValues.length; i++) {
      const partJson = {};
      let partHiddenUpstream = false;
      let partHiddenLocally = false;

      let upstreamValue = split.upstream ? split.upstream[i] : null;
      let localValue = split.local ? split.local[i] : null;
      const downstreamValue = split.downstream ? split.downstream[i] : null;
      const browserValue = split.browser ? split.browser[i] : null;
      const hasUpstream = hasValue(upstreamValue);
      const hasLocal = hasValue(localValue);
      const hasDownstream = hasValue(downstreamValue);
      const hasBrowser = hasValue(browserValue);

      if (!partHiddenUpstream && hasUpstream) {
        partHiddenUpstream = upstreamValue.indexOf(COMMENT_START) === 0;
      }

      if (partHiddenUpstream) {
        upstreamValue = _.trim(upstreamValue, COMMENT_CHARS);
      }

      if (!partHiddenLocally && hasLocal) {
        partHiddenLocally = localValue.indexOf(COMMENT_START) === 0;
      }

      if (partHiddenLocally) {
        localValue = _.trim(localValue, COMMENT_CHARS);
      }

      if (partHiddenLocally || (!hasLocal && partHiddenUpstream)) {
        partJsonLocal(partJson)._hidden = true;
      }

      if (partHiddenUpstream) {
        partJsonUpstream(partJson)._hidden = true;
      }

      if (hasUpstream) {
        const cssObj = CssUtils.toWebflowCssValueObject(upstreamValue);

        // Attach the upstream source of the value
        cssObj.source = styleData.upstream.source;

        // Store with a marker
        partJsonUpstream(partJson).css = cssObj;
      }

      if (hasLocal) {
        partJsonLocal(partJson).css =
          CssUtils.toWebflowCssValueObject(localValue);
      }

      if (hasDownstream) {
        const cssObj = CssUtils.toWebflowCssValueObject(downstreamValue);

        // Attach the upstream source of the value
        cssObj.source = styleData.downstream.source;

        // Store with a marker
        partJsonDownstream(partJson).css = cssObj;
      }

      if (hasBrowser) {
        partJsonBrowser(partJson).css =
          CssUtils.toWebflowCssValueObject(browserValue);
      }

      parts.push(partJson);
    }

    return parts;
  }

  if (split.local) {
    local = parseMode(StreamTypes.LOCAL);
  }
  if (split.upstream) {
    upstream = parseMode(StreamTypes.UPSTREAM);
  }
  if (split.downstream) {
    downstream = parseMode(StreamTypes.DOWNSTREAM);
  }

  // Walk through each part's CSS and parse into exact part details
  if (decorator) {
    _.each(StreamTypes.CASCADE_CHAIN, function (mode) {
      [local, upstream, downstream].forEach(function (parts) {
        _.each(parts, function (partJson) {
          const partJsonMode = partJson[mode];
          const partCss = partJsonMode && partJsonMode.css;
          if (!partCss) {
            return;
          }

          // Call extra decoration logic
          decorator && decorator(partCss.value, mode, partJson);
        });
      });
    });
  }

  // null means no value seen at this layer
  // empty array means explicit 'none' value seen
  // non-empty array means actual values
  const result = {
    local: null,
    upstream: null,
    downstream: null,
  };

  if (local.length > 0) {
    result.local = local;
  } else if (explicitLocalNoneOrNormal) {
    result.local = [];
    if (explicitNoneOrNormalSource) {
      result.local.source = explicitNoneOrNormalSource;
    }
  }

  if (
    explicitUpstreamNoneOrNormal &&
    !(
      upstream.length > 0 &&
      upstream.every(function (part) {
        return part.upstream._hidden;
      })
    )
  ) {
    result.upstream = [];
    if (explicitNoneOrNormalSource) {
      result.upstream.source = explicitNoneOrNormalSource;
    }
  } else if (upstream.length > 0) {
    result.upstream = upstream;
  }

  if (downstream.length > 0) {
    result.downstream = downstream;
  }

  return result;
}

/**
 * Parses style data for complex multi-value parts (transition, background-*).
 * @param  {Object}   styleData     The original values from StyleUtils.buildStyleMap.
 * @param  {Array}    subvalues     An array of Objects that contain properties the subvalues to be retrieved. Must have 'name' and 'reset'. 'skipParse' is optional.
 * @param  {String}   mainSubvalue  The name of the main subvalue (e.g. 'image' for backgrounds, 'property' for transition).
 * @param  {Function} decorator     A callback to call to decorate each part JSON with additional properties.
 * @return {Object}                 Parsed JSON part information.
 */
export function parseComplexParts(
  styleData,
  subvalues,
  mainSubvalue,
  decorator
) {
  let local = [];
  let upstream = [];
  let downstream = [];

  let explicitLocalNoneOrNormal = false;
  let explicitUpstreamNoneOrNormal = false;
  let explicitNoneOrNormalSource = null;

  /**
   * styleData is in the following format:
   *
   * upstream: {
   *   property: { value: 'margin, padding', source: StyleRule },
   *   duration: { value: '0.25s, 1s', source: StyleRule },
   *   timing: { value: 'ease-in, ease', source: StyleRule }
   * },
   * local: {
   *   duration: { value: '2000ms, 1s' }
   * }
   */

  // Walk through each cascade mode (upstream, local, downstream) and each subvalue (property, duration, etc)
  // and split values up into discrete chunks
  const split = {};
  _.each(StreamTypes.CASCADE_CHAIN_BROWSER, function (mode) {
    // Look up the mode (e.g. 'upstream') and see if there's any CSS style data applied
    const modeData = styleData[mode];
    if (!modeData) {
      return;
    }

    // Create a bucket for this mode's subvalues
    split[mode] = split[mode] || {};

    const isUpstream = mode === StreamTypes.UPSTREAM;
    let mainSubvalueIsNull = true;

    // Walk through each subvalue split up CSS values into parts (e.g. 'margin, padding' becomes ['margin', 'padding'])
    _.each(subvalues, function (subvalue, index) {
      const objValue = modeData[subvalue.name];
      let cssValue = objValue ? objValue.value : '';
      const isMainSubvalue = index === 0;
      const isSecondarySubvalue = !isMainSubvalue;

      // Determine if the main subvalue (e.g. 'image' for backgrounds, 'property' for transitions) is null,
      // because that factors into whether we mark the master 'none' value
      if (isMainSubvalue) {
        mainSubvalueIsNull = !hasValue(cssValue);
      }

      const upstreamReset = isUpstream && cssValue === subvalue.reset;
      const commentReset = _.startsWith(cssValue, subvalue.reset + ' /*');
      const explicitNone =
        (isMainSubvalue || (isSecondarySubvalue && mainSubvalueIsNull)) &&
        !commentReset;

      // Determine if the CSS value has a "none" override with other stuff (e.g. commented parts) afterwards
      if (upstreamReset || commentReset) {
        // Only set 'none' value if:
        // 1. The main subvalue (e.g. 'transition-property' or 'background-image' is set to 'none')
        // 2. A secondary subvalue is reset AND the main subvalue does not not have a value
        if (explicitNone) {
          if (isUpstream) {
            explicitUpstreamNoneOrNormal = true;
          } else {
            explicitLocalNoneOrNormal = true;
          }
          explicitNoneOrNormalSource = objValue.source;
        }

        // Only trim out the reset value if it doesn't look like a 'none' reset
        // This safeguards against proper values that look like reset values (e.g. background-position: 'left top') from
        // being treated as reset values
        if (explicitNone || commentReset) {
          const slicedCssValue = _.trim(
            strRight(cssValue, subvalue.reset + (isUpstream ? '' : ' '))
          );
          if (slicedCssValue) {
            cssValue = slicedCssValue;
          }
        }
      }

      // Save the split value
      split[mode][subvalue.name] = splitCssValue(cssValue, ',');
    });
  });

  function parseMode(mode) {
    const splitValues = split[mode];

    const parts = [];

    // If there is a none reset, use that
    if (
      splitValues[mainSubvalue].length > 0 &&
      (splitValues[mainSubvalue][0] === NONE ||
        splitValues[mainSubvalue][0] === NORMAL)
    ) {
      const isUpstream = mode === StreamTypes.UPSTREAM;

      if (isUpstream) {
        explicitUpstreamNoneOrNormal = true;
      } else {
        explicitLocalNoneOrNormal = true;
      }

      return parts;
    }

    // Create a JSON object for each part in the list
    const numItems = (function () {
      let result = 0;

      for (const k in splitValues) {
        result = Math.max(result, splitValues[k].length);
      }

      return result;
    })();
    for (let i = 0; i < numItems; i++) {
      const partJson = {};
      let partHiddenUpstream = false;
      let partHiddenLocally = false;
      let localSubvalueExists = false;

      // Walk through each subvalue and determine if it's inherited/local/both
      _.each(subvalues, function (subvalue) {
        let upstreamValue = split.upstream
          ? split.upstream[subvalue.name][i]
          : null;
        let localValue = split.local ? split.local[subvalue.name][i] : null;
        const downstreamValue = split.downstream
          ? split.downstream[subvalue.name][i]
          : null;
        const browserValue = split.browser
          ? split.browser[subvalue.name][i]
          : null;
        const hasUpstream = hasValue(upstreamValue);
        const hasLocal = hasValue(localValue);
        const hasDownstream = hasValue(downstreamValue);
        const hasBrowser = hasValue(browserValue);

        localSubvalueExists = localSubvalueExists || hasLocal;

        if (!partHiddenUpstream && hasUpstream) {
          partHiddenUpstream = upstreamValue.indexOf('/*') === 0;
        }

        if (partHiddenUpstream) {
          upstreamValue = _.trim(upstreamValue, COMMENT_CHARS);
        }

        if (!partHiddenLocally && hasLocal) {
          partHiddenLocally = localValue.indexOf('/*') === 0;
        }

        if (partHiddenLocally) {
          localValue = _.trim(localValue, COMMENT_CHARS);
        }

        if (hasUpstream) {
          const cssObj = CssUtils.toWebflowCssValueObject(
            upstreamValue,
            subvalue.skipParse
          );

          // Attach the upstream source of the value
          cssObj.source = styleData.upstream[subvalue.name].source;

          // Store with a marker
          partJsonUpstream(partJson)[subvalue.name] = cssObj;
        }

        if (hasLocal) {
          partJsonLocal(partJson)[subvalue.name] =
            CssUtils.toWebflowCssValueObject(localValue, subvalue.skipParse);
        }

        if (hasDownstream) {
          const cssObj = CssUtils.toWebflowCssValueObject(
            downstreamValue,
            subvalue.skipParse
          );

          // Attach the downstreams source of the value
          cssObj.source = styleData.downstream[subvalue.name].source;

          // Store with a marker
          partJsonDownstream(partJson)[subvalue.name] = cssObj;
        }

        if (hasBrowser) {
          partJsonBrowser(partJson)[subvalue.name] =
            CssUtils.toWebflowCssValueObject(browserValue, subvalue.skipParse);
        }
      });

      if (partHiddenLocally || (!localSubvalueExists && partHiddenUpstream)) {
        partJsonLocal(partJson)._hidden = true;
      }

      if (partHiddenUpstream) {
        partJsonUpstream(partJson)._hidden = true;
      }

      parts.push(partJson);
    }

    return parts;
  }

  if (split.local) {
    local = parseMode(StreamTypes.LOCAL);
  }
  if (split.upstream) {
    upstream = parseMode(StreamTypes.UPSTREAM);
  }
  if (split.downstream) {
    downstream = parseMode(StreamTypes.DOWNSTREAM);
  }

  // Walk through each part's CSS and parse into exact part details
  if (decorator) {
    const decorate = (mode, parts) => {
      _.each(parts, function (partJson) {
        _.each(subvalues, function (subvalue) {
          const subvalueJsonMode = partJson[mode];
          const subvalueCss =
            subvalueJsonMode && subvalueJsonMode[subvalue.name];
          if (!subvalueCss) {
            return;
          }

          decorator(subvalueCss.value, subvalue.name, mode, partJson);
        });
      });
    };

    _.each(StreamTypes.CASCADE_CHAIN_BROWSER, function (mode) {
      decorate(mode, local);
      decorate(mode, upstream);
      decorate(mode, downstream);
    });
  }

  // null means no value seen at this layer
  // empty array means explicit 'none' value seen
  // non-empty array means actual values
  const result = {
    local: null,
    upstream: null,
    downstream: null,
  };

  if (local.length > 0) {
    result.local = local;
  } else if (explicitLocalNoneOrNormal) {
    result.local = [];
    if (explicitNoneOrNormalSource) {
      result.local.source = explicitNoneOrNormalSource;
    }
  }

  if (
    explicitUpstreamNoneOrNormal &&
    !(
      upstream.length > 0 &&
      upstream.every(function (part) {
        return part.upstream._hidden;
      })
    )
  ) {
    result.upstream = [];
    if (explicitNoneOrNormalSource) {
      result.upstream.source = explicitNoneOrNormalSource;
    }
  } else if (upstream.length > 0) {
    result.upstream = upstream;
  }

  if (downstream.length > 0) {
    result.downstream = downstream;
  }

  return result;
}
